const warnedImageIds = new Set();

// Exported for tests only: resets the module-level warn dedup state.
export const __resetSharetribeImageWarnings = () => warnedImageIds.clear();

export const getSharetribeImageProps = (image, selectedVariants = []) => {
  if (!image.id) {
    throw new Error('Image is missing id');
  }

  if (selectedVariants.length === 0) {
    throw new Error('No variants selected');
  }

  const availableVariants = image.attributes.variants;
  const availableVariantKeys = Object.keys(availableVariants);

  const variantExisting = selectedVariants.some((variant) =>
    availableVariantKeys.includes(variant),
  );

  // None of the requested variants exist on this image. Rather than crashing the
  // whole ListingPage render (see VANSITE-FRONTEND-48D / 4NT), fall back to whatever
  // variants the image actually has.
  if (!variantExisting) {
    const imageId = image.id.uuid || image.id;

    if (!warnedImageIds.has(imageId)) {
      warnedImageIds.add(imageId);
      console.warn(
        `useSharetribeImage: none of the requested variants (${selectedVariants.join(
          ', ',
        )}) exist on image ${imageId}. Falling back to available variants.`,
      );
    }

    if (availableVariantKeys.length === 0) {
      return { srcset: '', src: undefined, blurSrc: undefined };
    }

    const fallbackVariants = Object.values(availableVariants).sort((a, b) => a.width - b.width);

    const srcset = fallbackVariants
      .map((variant) => `${variant.url} ${variant.width}w`)
      .join(', ');
    const blurSrc = fallbackVariants[0]?.url;
    const src =
      fallbackVariants[Math.floor(fallbackVariants.length / 2)]?.url || fallbackVariants[0]?.url;

    return { srcset, src, blurSrc };
  }

  let variants = Object.entries(availableVariants)
    .filter(([key]) => selectedVariants.includes(key))
    .map(([_, value]) => value);

  // Sort variants by width ascending
  variants = variants.sort((a, b) => a.width - b.width);

  const srcset = variants.map((variant) => `${variant.url} ${variant.width}w`).join(', ');

  const blurSrc = variants[0]?.url; // Smallest for blur
  const src = variants[Math.floor(variants.length / 2)]?.url || variants[0]?.url; // Medium or smallest as fallback

  return {
    srcset,
    src,
    blurSrc,
  };
};

const useSharetribeImage = (image, selectedVariants) =>
  getSharetribeImageProps(image, selectedVariants);

export default useSharetribeImage;
